import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { 
  Plus, Search, Edit, Trash2, Calendar, Copy,
  ExternalLink, Globe, Building, CheckCircle, XCircle, Clock, 
  RefreshCw, Megaphone, Star, ChevronDown, ChevronUp, Save, X, MapPin
} from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../../styles/OpenCallsAdmin.css';

const OpenCallsAdmin = () => {
  const navigate = useNavigate();
  const [callouts, setCallouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc'); // Latest first
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Expandable row state
  const [expandedId, setExpandedId] = useState(null);
  const [inlineEdits, setInlineEdits] = useState({});
  const [saving, setSaving] = useState(false);

  // Bulk selection state
  const [selectedCalls, setSelectedCalls] = useState(new Set());

  // Scraping state
  const [scraping, setScraping] = useState(false);

  // Copy URL feedback
  const [copiedId, setCopiedId] = useState(null);

  // Gallery selector state
  const [galleries, setGalleries] = useState([]);
  const [gallerySearch, setGallerySearch] = useState('');
  const [galleryDropdownOpen, setGalleryDropdownOpen] = useState(false);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [newGallery, setNewGallery] = useState({ name: '', address: '', website: '', type: 'Gallery' });
  const [creatingGallery, setCreatingGallery] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    internal: 0,
    externalManaged: 0,
    externalScraped: 0,
    pending: 0,
    live: 0
  });

  const fetchCallouts = useCallback(async () => {
    try {
      setLoading(true);
      
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build query
      let query = supabase
        .from('submissions_details')
        .select('*', { count: 'exact' })
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to);

      // Apply filters
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,venue_location.ilike.%${searchQuery}%`);
      }
      
      if (sourceFilter !== 'all') {
        query = query.eq('source_type', sourceFilter);
      }
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      if (approvalFilter !== 'all') {
        query = query.eq('approval_status', approvalFilter);
      }

      const { data, error, count } = await query;
      
      if (error) throw error;
      
      setCallouts(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching callouts:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sourceFilter, statusFilter, approvalFilter, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      // Get counts by source type
      const { data: sourceData } = await supabase
        .from('submissions_details')
        .select('source_type');

      const { data: statusData } = await supabase
        .from('submissions_details')
        .select('status, approval_status');

      const sources = sourceData || [];
      const statuses = statusData || [];

      setStats({
        total: sources.length,
        internal: sources.filter(s => !s.source_type || s.source_type === 'internal').length,
        externalManaged: sources.filter(s => s.source_type === 'external_managed').length,
        externalScraped: sources.filter(s => s.source_type === 'external_scraped').length,
        pending: statuses.filter(s => s.approval_status === 'pending').length,
        live: statuses.filter(s => s.status === 'live').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchGalleries = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('galleries')
        .select('gallery_id, name, address, type, website, approved')
        .eq('approved', true)
        .order('name', { ascending: true });
      if (error) throw error;
      setGalleries(data || []);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    }
  }, []);

  useEffect(() => {
    fetchCallouts();
    fetchStats();
    fetchGalleries();
  }, [fetchCallouts, fetchStats, fetchGalleries]);

  const handleCreateCallout = () => {
    navigate('/admin/open-calls/create');
  };

  const handleEditCallout = (callout) => {
    navigate(`/admin/open-calls/${callout.submission_id}/edit`);
  };

  const handleDeleteCallout = async (callout) => {
    if (!window.confirm(`Are you sure you want to delete "${callout.title}"?`)) return;
    
    try {
      const { error } = await supabase
        .from('submissions_details')
        .delete()
        .eq('submission_id', callout.submission_id);
      
      if (error) throw error;
      
      fetchCallouts();
      fetchStats();
    } catch (error) {
      console.error('Error deleting callout:', error);
      alert('Failed to delete callout');
    }
  };

  const handleApprove = async (callout) => {
    try {
      // Set open_date to 1 hour ago so it shows up immediately in the app
      const openDate = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from('submissions_details')
        .update({ 
          approval_status: 'approved',
          status: 'live',
          open_date: openDate
        })
        .eq('submission_id', callout.submission_id);
      
      if (error) throw error;
      
      setExpandedId(null);
      setInlineEdits({});
      fetchCallouts();
      fetchStats();
    } catch (error) {
      console.error('Error approving callout:', error);
      alert('Failed to approve callout');
    }
  };

  const handleReject = async (callout) => {
    try {
      const { error } = await supabase
        .from('submissions_details')
        .update({ 
          approval_status: 'rejected',
          status: 'rejected'
        })
        .eq('submission_id', callout.submission_id);
      
      if (error) throw error;
      
      setExpandedId(null);
      setInlineEdits({});
      fetchCallouts();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting callout:', error);
      alert('Failed to reject callout');
    }
  };

  const toggleExpand = (callout) => {
    if (expandedId === callout.submission_id) {
      setExpandedId(null);
      setInlineEdits({});
    } else {
      setExpandedId(callout.submission_id);
      setInlineEdits({
        title: callout.title || '',
        external_url: callout.external_url || '',
        submission_deadline: callout.submission_deadline ? callout.submission_deadline.split('T')[0] : '',
        entry_fee_description: callout.entry_fee_description || '',
        venue_location: callout.venue_location || '',
        scope: callout.scope || '',
        source_type: callout.source_type || 'external_scraped',
        gallery_id: callout.gallery_id || null,
      });
      setGallerySearch('');
      setGalleryDropdownOpen(false);
    }
  };

  const handleInlineChange = (field, value) => {
    setInlineEdits(prev => ({ ...prev, [field]: value }));
  };

  const handleInlineSave = async (callout) => {
    try {
      setSaving(true);
      const updates = {
        title: inlineEdits.title || null,
        external_url: inlineEdits.external_url || null,
        submission_deadline: inlineEdits.submission_deadline ? new Date(inlineEdits.submission_deadline).toISOString() : null,
        entry_fee_description: inlineEdits.entry_fee_description || null,
        venue_location: inlineEdits.venue_location || null,
        scope: inlineEdits.scope || null,
        source_type: inlineEdits.source_type || null,
        gallery_id: inlineEdits.gallery_id || null,
      };

      const { error } = await supabase
        .from('submissions_details')
        .update(updates)
        .eq('submission_id', callout.submission_id);

      if (error) throw error;

      fetchCallouts();
      fetchStats();
    } catch (error) {
      console.error('Error saving inline edits:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // --- Bulk actions ---
  const toggleSelect = (submissionId, e) => {
    e.stopPropagation();
    setSelectedCalls(prev => {
      const next = new Set(prev);
      if (next.has(submissionId)) next.delete(submissionId);
      else next.add(submissionId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCalls.size === callouts.length && callouts.length > 0) {
      setSelectedCalls(new Set());
    } else {
      setSelectedCalls(new Set(callouts.map(c => c.submission_id)));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedCalls.size === 0) return;
    try {
      const openDate = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from('submissions_details')
        .update({ approval_status: 'approved', status: 'live', open_date: openDate })
        .in('submission_id', Array.from(selectedCalls));
      if (error) throw error;
      setSelectedCalls(new Set());
      fetchCallouts();
      fetchStats();
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('Failed to approve selected calls');
    }
  };

  const handleBulkReject = async () => {
    if (selectedCalls.size === 0) return;
    try {
      const { error } = await supabase
        .from('submissions_details')
        .update({ approval_status: 'rejected', status: 'rejected' })
        .in('submission_id', Array.from(selectedCalls));
      if (error) throw error;
      setSelectedCalls(new Set());
      fetchCallouts();
      fetchStats();
    } catch (error) {
      console.error('Error bulk rejecting:', error);
      alert('Failed to reject selected calls');
    }
  };

  // --- Scrape Now ---
  const handleScrapeNow = async () => {
    setScraping(true);
    try {
      const response = await fetch(
        'https://api.vibrantcast.com/functions/v1/scrape-entrythingy?state=IL&maxPages=3&enrichVenues=true',
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const result = await response.json();
      if (result.success) {
        const galleryInfo = result.galleries
          ? `\nGalleries: ${result.galleries.matched} matched, ${result.galleries.created} new venues discovered`
          : '';
        alert(`Scraped ${result.scraped} calls.\nInserted: ${result.inserted}, Updated: ${result.updated}${galleryInfo}`);
        fetchCallouts();
        fetchStats();
      } else {
        alert('Scrape failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Scrape error:', error);
      alert('Error running scraper');
    } finally {
      setScraping(false);
    }
  };

  // --- Copy URL ---
  const handleCopyUrl = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  // --- Deadline countdown ---
  const formatDeadlineCountdown = (dateStr) => {
    if (!dateStr) return null;
    const deadline = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return <span className="deadline-expired">Expired</span>;
    if (diff === 0) return <span className="deadline-today">Today!</span>;
    if (diff <= 7) return <span className="deadline-soon">{diff}d left</span>;
    return null;
  };

  const getSourceBadge = (sourceType) => {
    switch (sourceType) {
      case 'external_managed':
        return <span className="badge badge-blue"><Building size={12} /> External Managed</span>;
      case 'external_scraped':
        return <span className="badge badge-purple"><Globe size={12} /> Scraped</span>;
      default:
        return <span className="badge badge-green"><Megaphone size={12} /> Internal</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return <span className="badge badge-green"><CheckCircle size={12} /> Live</span>;
      case 'draft':
        return <span className="badge badge-gray"><Clock size={12} /> Draft</span>;
      case 'closed':
        return <span className="badge badge-red"><XCircle size={12} /> Closed</span>;
      case 'rejected':
        return <span className="badge badge-red"><XCircle size={12} /> Rejected</span>;
      default:
        return <span className="badge badge-gray">{status || 'Unknown'}</span>;
    }
  };

  const getApprovalBadge = (approvalStatus) => {
    switch (approvalStatus) {
      case 'approved':
        return <span className="badge badge-green"><CheckCircle size={12} /> Approved</span>;
      case 'pending':
        return <span className="badge badge-yellow"><Clock size={12} /> Pending</span>;
      case 'rejected':
        return <span className="badge badge-red"><XCircle size={12} /> Rejected</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Free';
    if (price === 0) return 'Free';
    return `$${Number(price).toFixed(0)}`;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="open-calls-admin-container">
      <div className="open-calls-admin-content">
        {/* Header */}
        <div className="admin-header">
          <div className="header-left">
            <Megaphone className="header-icon" size={28} />
            <div>
              <h1>Open Calls Manager</h1>
              <p>Manage internal and external artist callouts</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              className="btn-secondary scrape-now-btn" 
              onClick={handleScrapeNow}
              disabled={scraping}
            >
              <RefreshCw size={16} className={scraping ? 'spinning' : ''} />
              {scraping ? 'Scraping...' : 'Scrape Now (IL)'}
            </button>
            <button className="btn-primary" onClick={handleCreateCallout}>
              <Plus size={18} />
              Add External Callout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon blue"><Megaphone size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Callouts</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green"><CheckCircle size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.live}</span>
              <span className="stat-label">Live Now</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon purple"><Building size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.externalManaged}</span>
              <span className="stat-label">External Managed</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon yellow"><Clock size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.pending}</span>
              <span className="stat-label">Pending Review</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search callouts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <select 
              value={sourceFilter} 
              onChange={(e) => setSourceFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sources</option>
              <option value="internal">Internal</option>
              <option value="external_managed">External Managed</option>
              <option value="external_scraped">External Scraped</option>
            </select>
            
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="live">Live</option>
              <option value="draft">Draft</option>
              <option value="closed">Closed</option>
            </select>
            
            <select 
              value={approvalFilter} 
              onChange={(e) => setApprovalFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Approval</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>

            <select 
              value={`${sortBy}-${sortOrder}`} 
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="filter-select"
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="submission_deadline-asc">Deadline (Soonest)</option>
              <option value="submission_deadline-desc">Deadline (Latest)</option>
              <option value="title-asc">Title A-Z</option>
              <option value="title-desc">Title Z-A</option>
            </select>
          </div>

          <button className="btn-secondary" onClick={() => { fetchCallouts(); fetchStats(); }}>
            <RefreshCw size={16} />
            Refresh
          </button>

          {/* Bulk actions bar */}
          {selectedCalls.size > 0 && (
            <div className="bulk-actions-bar">
              <span className="bulk-count">{selectedCalls.size} selected</span>
              <button className="inline-approve-btn" onClick={handleBulkApprove}>
                <CheckCircle size={14} />
                Approve All
              </button>
              <button className="inline-reject-btn" onClick={handleBulkReject}>
                <XCircle size={14} />
                Reject All
              </button>
              <button className="inline-cancel-btn" onClick={() => setSelectedCalls(new Set())}>
                <X size={14} />
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <LoadingSpinner />
              <p>Loading callouts...</p>
            </div>
          ) : callouts.length === 0 ? (
            <div className="empty-state">
              <Megaphone size={48} className="empty-icon" />
              <h3>No Callouts Found</h3>
              <p>Create your first external callout or adjust your filters.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '36px' }}>
                    <input
                      type="checkbox"
                      checked={selectedCalls.size === callouts.length && callouts.length > 0}
                      onChange={toggleSelectAll}
                      className="bulk-checkbox"
                    />
                  </th>
                  <th style={{ width: '30px' }}></th>
                  <th>Title</th>
                  <th>Source</th>
                  <th>Deadline</th>
                  <th>Fee</th>
                  <th>Status</th>
                  <th>Approval</th>
                </tr>
              </thead>
              <tbody>
                {callouts.map((callout) => (
                  <React.Fragment key={callout.submission_id}>
                    <tr 
                      className={`${expandedId === callout.submission_id ? 'row-expanded' : ''} ${selectedCalls.has(callout.submission_id) ? 'row-selected' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleExpand(callout)}
                    >
                      <td style={{ width: '36px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedCalls.has(callout.submission_id)}
                          onChange={(e) => toggleSelect(callout.submission_id, e)}
                          className="bulk-checkbox"
                        />
                      </td>
                      <td style={{ width: '30px', textAlign: 'center' }}>
                        {expandedId === callout.submission_id 
                          ? <ChevronUp size={16} style={{ color: '#64748b' }} />
                          : <ChevronDown size={16} style={{ color: '#64748b' }} />
                        }
                      </td>
                      <td className="title-cell">
                        <div className="title-content">
                          {callout.featured && <Star size={14} className="featured-star" />}
                          <span className="title-text">{callout.title || 'Untitled'}</span>
                        </div>
                        {callout.venue_location && (
                          <span className="venue-text">{callout.venue_location}</span>
                        )}
                      </td>
                      <td>{getSourceBadge(callout.source_type)}</td>
                      <td>
                        <div>{formatDate(callout.submission_deadline)}</div>
                        {formatDeadlineCountdown(callout.submission_deadline)}
                      </td>
                      <td>{callout.entry_fee_description || formatPrice(callout.submission_price)}</td>
                      <td>{getStatusBadge(callout.status)}</td>
                      <td>{getApprovalBadge(callout.approval_status)}</td>
                    </tr>
                    {expandedId === callout.submission_id && (
                      <tr className="expanded-detail-row">
                        <td colSpan={8} style={{ padding: 0 }}>
                          <div className="inline-edit-panel" onClick={(e) => e.stopPropagation()}>
                            {/* URL row - prominent */}
                            <div className="inline-url-row">
                              <Globe size={16} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                              <input
                                type="url"
                                value={inlineEdits.external_url}
                                onChange={(e) => handleInlineChange('external_url', e.target.value)}
                                placeholder="External URL"
                                className="inline-input inline-url-input"
                              />
                              {inlineEdits.external_url && (
                                <>
                                  <button
                                    className="inline-copy-url-btn"
                                    onClick={() => handleCopyUrl(inlineEdits.external_url, callout.submission_id)}
                                    title="Copy URL to clipboard"
                                  >
                                    <Copy size={14} />
                                    {copiedId === callout.submission_id ? 'Copied!' : 'Copy'}
                                  </button>
                                  <a
                                    href={inlineEdits.external_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-check-url-btn"
                                  >
                                    <ExternalLink size={14} />
                                    Open
                                  </a>
                                </>
                              )}
                            </div>

                            {/* Gallery selector */}
                            <div className="inline-gallery-row">
                              <Building size={16} style={{ color: '#6366f1', flexShrink: 0 }} />
                              <div className="inline-gallery-selector">
                                {inlineEdits.gallery_id ? (
                                  <div className="inline-gallery-selected">
                                    <span className="inline-gallery-name">
                                      {galleries.find(g => g.gallery_id === inlineEdits.gallery_id)?.name || `Gallery #${inlineEdits.gallery_id}`}
                                    </span>
                                    <span className="inline-gallery-address">
                                      {galleries.find(g => g.gallery_id === inlineEdits.gallery_id)?.address || ''}
                                    </span>
                                    <button
                                      className="inline-gallery-clear"
                                      onClick={() => handleInlineChange('gallery_id', null)}
                                      title="Remove gallery"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="inline-gallery-search-wrap">
                                    <input
                                      type="text"
                                      value={gallerySearch}
                                      onChange={(e) => {
                                        setGallerySearch(e.target.value);
                                        setGalleryDropdownOpen(true);
                                      }}
                                      onFocus={() => setGalleryDropdownOpen(true)}
                                      placeholder="Search galleries to link..."
                                      className="inline-input inline-gallery-input"
                                    />
                                    {galleryDropdownOpen && (
                                      <div className="inline-gallery-dropdown">
                                        {galleries
                                          .filter(g => {
                                            if (!gallerySearch) return true;
                                            const term = gallerySearch.toLowerCase();
                                            return g.name?.toLowerCase().includes(term) || g.address?.toLowerCase().includes(term);
                                          })
                                          .slice(0, 8)
                                          .map(g => (
                                            <div
                                              key={g.gallery_id}
                                              className="inline-gallery-option"
                                              onClick={() => {
                                                handleInlineChange('gallery_id', g.gallery_id);
                                                if (!inlineEdits.venue_location && g.address) {
                                                  handleInlineChange('venue_location', g.address);
                                                }
                                                setGallerySearch('');
                                                setGalleryDropdownOpen(false);
                                              }}
                                            >
                                              <span className="gallery-option-name">{g.name}</span>
                                              {g.address && <span className="gallery-option-address"><MapPin size={10} /> {g.address}</span>}
                                            </div>
                                          ))}
                                        {galleries.filter(g => {
                                          if (!gallerySearch) return true;
                                          const term = gallerySearch.toLowerCase();
                                          return g.name?.toLowerCase().includes(term) || g.address?.toLowerCase().includes(term);
                                        }).length === 0 && (
                                          <div className="inline-gallery-no-results">
                                            No galleries found
                                          </div>
                                        )}
                                        <div
                                          className="inline-gallery-add-new"
                                          onClick={() => {
                                            setNewGallery({ name: gallerySearch, address: '', website: '', type: 'Gallery' });
                                            setShowCreateGallery(true);
                                            setGalleryDropdownOpen(false);
                                          }}
                                        >
                                          <Plus size={14} />
                                          Add New Gallery{gallerySearch ? `: "${gallerySearch}"` : ''}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Editable fields grid */}
                            <div className="inline-edit-grid">
                              <div className="inline-field">
                                <label>Title</label>
                                <input
                                  type="text"
                                  value={inlineEdits.title}
                                  onChange={(e) => handleInlineChange('title', e.target.value)}
                                  className="inline-input"
                                />
                              </div>
                              <div className="inline-field">
                                <label>Deadline</label>
                                <input
                                  type="date"
                                  value={inlineEdits.submission_deadline}
                                  onChange={(e) => handleInlineChange('submission_deadline', e.target.value)}
                                  className="inline-input"
                                />
                              </div>
                              <div className="inline-field">
                                <label>Fee Description</label>
                                <input
                                  type="text"
                                  value={inlineEdits.entry_fee_description}
                                  onChange={(e) => handleInlineChange('entry_fee_description', e.target.value)}
                                  placeholder="e.g. $35 for 3 pieces"
                                  className="inline-input"
                                />
                              </div>
                              <div className="inline-field">
                                <label>Venue / Location</label>
                                <input
                                  type="text"
                                  value={inlineEdits.venue_location}
                                  onChange={(e) => handleInlineChange('venue_location', e.target.value)}
                                  placeholder="Location"
                                  className="inline-input"
                                />
                              </div>
                              <div className="inline-field">
                                <label>Scope</label>
                                <select
                                  value={inlineEdits.scope}
                                  onChange={(e) => handleInlineChange('scope', e.target.value)}
                                  className="inline-input"
                                >
                                  <option value="">—</option>
                                  <option value="National">National</option>
                                  <option value="International">International</option>
                                  <option value="Regional">Regional</option>
                                  <option value="Local">Local</option>
                                </select>
                              </div>
                              <div className="inline-field">
                                <label>Source Type</label>
                                <select
                                  value={inlineEdits.source_type}
                                  onChange={(e) => handleInlineChange('source_type', e.target.value)}
                                  className="inline-input"
                                >
                                  <option value="external_scraped">External Scraped</option>
                                  <option value="external_managed">External Managed</option>
                                </select>
                              </div>
                            </div>

                            {/* Action bar */}
                            <div className="inline-actions-bar">
                              <div className="inline-actions-left">
                                {callout.approval_status === 'pending' && (
                                  <>
                                    <button className="inline-approve-btn" onClick={() => handleApprove(callout)}>
                                      <CheckCircle size={16} />
                                      Approve &amp; Go Live
                                    </button>
                                    <button className="inline-reject-btn" onClick={() => handleReject(callout)}>
                                      <XCircle size={16} />
                                      Reject
                                    </button>
                                  </>
                                )}
                              </div>
                              <div className="inline-actions-right">
                                <button 
                                  className="inline-full-edit-btn"
                                  onClick={() => handleEditCallout(callout)}
                                >
                                  <Edit size={14} />
                                  Full Edit
                                </button>
                                <button 
                                  className="inline-delete-btn"
                                  onClick={() => handleDeleteCallout(callout)}
                                >
                                  <Trash2 size={14} />
                                  Delete
                                </button>
                                <button 
                                  className="inline-cancel-btn"
                                  onClick={() => { setExpandedId(null); setInlineEdits({}); }}
                                >
                                  <X size={14} />
                                  Cancel
                                </button>
                                <button 
                                  className="inline-save-btn"
                                  onClick={() => handleInlineSave(callout)}
                                  disabled={saving}
                                >
                                  <Save size={14} />
                                  {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Gallery Modal */}
        {showCreateGallery && (
          <div className="inline-gallery-modal-overlay" onClick={() => setShowCreateGallery(false)}>
            <div className="inline-gallery-modal" onClick={(e) => e.stopPropagation()}>
              <div className="inline-gallery-modal-header">
                <h3><Building size={18} /> Add New Gallery</h3>
                <button onClick={() => setShowCreateGallery(false)} className="inline-gallery-modal-close"><X size={18} /></button>
              </div>
              <div className="inline-gallery-modal-body">
                <div className="inline-field">
                  <label>Gallery Name *</label>
                  <input
                    type="text"
                    value={newGallery.name}
                    onChange={(e) => setNewGallery(prev => ({ ...prev, name: e.target.value }))}
                    className="inline-input"
                    placeholder="Gallery or venue name"
                  />
                </div>
                <div className="inline-field">
                  <label>Address</label>
                  <input
                    type="text"
                    value={newGallery.address}
                    onChange={(e) => setNewGallery(prev => ({ ...prev, address: e.target.value }))}
                    className="inline-input"
                    placeholder="Full address"
                  />
                </div>
                <div className="inline-field">
                  <label>Website</label>
                  <input
                    type="url"
                    value={newGallery.website}
                    onChange={(e) => setNewGallery(prev => ({ ...prev, website: e.target.value }))}
                    className="inline-input"
                    placeholder="https://..."
                  />
                </div>
                <div className="inline-field">
                  <label>Type</label>
                  <select
                    value={newGallery.type}
                    onChange={(e) => setNewGallery(prev => ({ ...prev, type: e.target.value }))}
                    className="inline-input"
                  >
                    <option value="Gallery">Gallery</option>
                    <option value="Museum">Museum</option>
                    <option value="Art Center">Art Center</option>
                    <option value="Cultural Center">Cultural Center</option>
                    <option value="University">University</option>
                    <option value="Online">Online</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="inline-gallery-modal-footer">
                <button onClick={() => setShowCreateGallery(false)} className="inline-cancel-btn">Cancel</button>
                <button
                  className="inline-save-btn"
                  disabled={!newGallery.name.trim() || creatingGallery}
                  onClick={async () => {
                    try {
                      setCreatingGallery(true);
                      const { data, error } = await supabase
                        .from('galleries')
                        .insert({
                          name: newGallery.name.trim(),
                          address: newGallery.address.trim() || null,
                          website: newGallery.website.trim() || null,
                          type: newGallery.type || null,
                          approved: true,
                        })
                        .select('gallery_id, name, address, type, website, approved')
                        .single();
                      if (error) throw error;
                      setGalleries(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
                      handleInlineChange('gallery_id', data.gallery_id);
                      if (!inlineEdits.venue_location && data.address) {
                        handleInlineChange('venue_location', data.address);
                      }
                      setShowCreateGallery(false);
                      setNewGallery({ name: '', address: '', website: '', type: 'Gallery' });
                    } catch (error) {
                      console.error('Error creating gallery:', error);
                      alert('Failed to create gallery: ' + error.message);
                    } finally {
                      setCreatingGallery(false);
                    }
                  }}
                >
                  <Save size={14} />
                  {creatingGallery ? 'Creating...' : 'Create & Link'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenCallsAdmin;
